import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { Badge, Text } from "@/components/ui";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type {
  Matter,
  MatterAssignee,
  Connection,
  MatterInsurance,
  MatterSettlementDemand,
  MatterSettlementOffer,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getInsuranceContactConnection } from "@/lib/utils/connections/insurance-contact-connections";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import {
  formatDate,
  formatContactsName,
  formatInsuranceTitle,
  formatLocalOrIntlPhoneNumber,
  formatUsersName,
} from "@/lib/utils/format";
import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import { ChevronRight, Mail, MessageSquareText, Phone } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { HeaderButton } from "../components/HeaderButton";
import {
  formatCurrency,
  getActiveAssignees,
  getInsuranceCarrierName,
  getLatestByDate,
  getSentSettlementDemands,
} from "../matter-overview-utils";
import {
  getPendingDemandStatusBadgeClass,
  type PendingDemandCoverageGroup,
} from "./pending-demand-coverage-utils";

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text
      as="p"
      variant="caption"
      color="muted"
      className="font-semibold tracking-[0.18em] uppercase"
    >
      {children}
    </Text>
  );
}

function formatLimitPair(
  perPerson?: number | null,
  perIncident?: number | null,
) {
  if (perPerson != null && perIncident != null) {
    return `${formatCompactLimit(perPerson)} / ${formatCompactLimit(perIncident)}`;
  }

  return formatCompactLimit(perPerson ?? perIncident);
}

function formatCompactLimit(value?: number | null) {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }

  if (Math.abs(value) >= 1000) {
    const compactValue = value / 1000;
    const formatted =
      compactValue % 1 === 0
        ? compactValue.toFixed(0)
        : compactValue.toFixed(1).replace(/\.0$/, "");

    return `$${formatted}k`;
  }

  return formatCurrency(value);
}

function getDemandAssignee(matter: Matter): MatterAssignee | undefined {
  const activeAssignees = getActiveAssignees(matter);

  return (
    activeAssignees.find(
      (assignee) => assignee.role.label === "Lead Attorney",
    ) ??
    activeAssignees.find(
      (assignee) => assignee.role.label === "Case Manager",
    ) ??
    activeAssignees[0]
  );
}

function getIsFirstPartyInsurance(matter: Matter, insurance?: MatterInsurance) {
  if (!insurance) {
    return false;
  }

  if (insurance.type === "first_party") return true;
  if (insurance.type === "third_party_liability") return false;

  const insuredConnection = getInsuranceContactConnection(insurance, "Insured");
  const clientConnection = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  );

  return (
    !!insuredConnection?.targetObjectId &&
    insuredConnection.targetObjectId === clientConnection?.contactId
  );
}

function getDemandInsuranceLimits(
  insurance: MatterInsurance | undefined,
  isFirstParty: boolean,
  coverageType?: string | null,
) {
  if (!insurance) {
    return undefined;
  }

  const normalizedCoverageType = coverageType?.trim();

  if (!isFirstParty) {
    const limits = formatLimitPair(
      insurance.policyLimitPerPerson,
      insurance.policyLimitPerIncident,
    );

    return limits === "—" ? undefined : limits;
  }

  if (normalizedCoverageType === "UM") {
    const limits = formatLimitPair(
      insurance.umPerPerson,
      insurance.umPerIncident,
    );

    return limits === "—" ? undefined : limits;
  }

  if (normalizedCoverageType === "UIM") {
    const limits = formatLimitPair(
      insurance.uimPerPerson,
      insurance.uimPerIncident,
    );

    return limits === "—" ? undefined : limits;
  }

  if (normalizedCoverageType === "MedPay") {
    const limit = formatCompactLimit(insurance.medPayLimit);

    return limit === "—" ? undefined : limit;
  }

  return undefined;
}

function getDemandInsuranceSummary(
  matter: Matter,
  demand: MatterSettlementDemand,
) {
  const insurance = matter.insurances.find(
    (item) => item.id === demand.insuranceId,
  );
  const carrier = getInsuranceCarrierName(matter, insurance);
  const isFirstParty = getIsFirstPartyInsurance(matter, insurance);
  const partyLabel = isFirstParty ? "1st Party" : "3rd Party";
  const coverageType = demand.coverageType?.trim();
  const partyCoverageLabel = coverageType
    ? `${partyLabel} ${coverageType}`
    : isFirstParty
      ? partyLabel
      : `${partyLabel} BI`;
  const limits = getDemandInsuranceLimits(
    insurance,
    isFirstParty,
    coverageType,
  );
  const claimNumber = insurance?.claimNumber?.trim();
  const claimLabel = `Claim # ${claimNumber || "—"}`;

  return [carrier, partyCoverageLabel, claimLabel, limits]
    .filter(Boolean)
    .join(" • ");
}

type DemandAdjusterDetail = {
  label: string;
  name: string;
  formattedPhone: string;
  phoneNumber?: string;
  contactId?: string;
  emailAddress?: string;
};

const DEMAND_INSURANCE_ADJUSTER_SLOTS: Array<{
  label: string;
  getConnection: (
    insuranceTitle: NonNullable<ReturnType<typeof formatInsuranceTitle>>,
  ) => Connection | undefined;
}> = [
  {
    label: "Bodily injury adjustor",
    getConnection: (insuranceTitle) => insuranceTitle.bodilyInjuryAdjuster,
  },
  {
    label: "Property damage adjustor",
    getConnection: (insuranceTitle) => insuranceTitle.propertyDamageAdjuster,
  },
  {
    label: "MedPay Adjustor",
    getConnection: (insuranceTitle) => insuranceTitle.medPayAdjuster,
  },
  {
    label: "Adjustor supervisor",
    getConnection: (insuranceTitle) => insuranceTitle.adjusterSupervisor,
  },
];

function getDemandInsuranceAdjusters(
  matter: Matter,
  demand: MatterSettlementDemand,
): DemandAdjusterDetail[] {
  const insurance = matter.insurances.find(
    (item) => item.id === demand.insuranceId,
  );
  const insuranceTitle = formatInsuranceTitle({
    insurance,
  });

  if (!insuranceTitle) {
    return [];
  }

  return DEMAND_INSURANCE_ADJUSTER_SLOTS.flatMap(({ label, getConnection }) => {
    const adjuster = getConnection(insuranceTitle);

    if (!adjuster) {
      return [];
    }

    const primaryPhone = adjuster.contact?.primaryPhone;
    const phoneNumber = primaryPhone?.number;
    const primaryEmail = adjuster.contact?.primaryEmail;

    return [
      {
        label,
        name: formatContactsName(adjuster.contact ?? undefined) ?? "",
        formattedPhone: primaryPhone
          ? formatLocalOrIntlPhoneNumber(primaryPhone)
          : "—",
        phoneNumber: phoneNumber ?? "",
        contactId: adjuster.targetObjectId,
        emailAddress: primaryEmail?.address ?? "",
      },
    ];
  });
}

function getDemandSettlementGroupKey(demand: MatterSettlementDemand) {
  const rawCoverageType = demand.coverageType ?? undefined;
  const isMedPay = rawCoverageType === "MedPay";
  const coverageType = rawCoverageType ?? (isMedPay ? "MedPay" : undefined);
  const coverageKey = coverageType ?? "none";

  return `${demand.insuranceId ?? ""}-${isMedPay}-${coverageKey}`;
}

function getOfferSettlementGroupKey(offer: MatterSettlementOffer) {
  const rawCoverageType = offer.coverageType ?? undefined;
  const isMedPay = rawCoverageType
    ? rawCoverageType === "MedPay"
    : (offer.medPay ?? false);
  const coverageType = rawCoverageType ?? (isMedPay ? "MedPay" : undefined);
  const coverageKey = coverageType ?? "none";

  return `${offer.insuranceId ?? ""}-${isMedPay}-${coverageKey}`;
}

function getAcceptedOfferForDemand(
  matter: Matter,
  demand: MatterSettlementDemand,
) {
  const demandGroupKey = getDemandSettlementGroupKey(demand);

  return getLatestByDate(
    matter.settlementOffers.filter(
      (offer) =>
        offer.accepted && getOfferSettlementGroupKey(offer) === demandGroupKey,
    ),
    (offer) => offer.acceptedDate ?? offer.dateReceived,
  );
}

function getAcceptedOfferBadgeLabel(
  demand: MatterSettlementDemand,
  offer: MatterSettlementOffer,
) {
  const demandAmount = demand.amount;
  const offerAmount = offer.amount;

  if (
    demandAmount != null &&
    offerAmount != null &&
    demandAmount === offerAmount
  ) {
    return "Accepted - Full Amount";
  }

  if (offerAmount == null) {
    return "Accepted";
  }

  return `Accepted - ${formatCurrency(offerAmount)}`;
}

function openAdjusterPhoneCall(phoneNumber?: string) {
  if (!phoneNumber || typeof window === "undefined") {
    return;
  }

  const sanitizedPhoneNumber = phoneNumber.replace(/[^0-9+]/g, "");

  if (!sanitizedPhoneNumber) {
    return;
  }

  window.open(`tel:${sanitizedPhoneNumber}`, "_self");
}

function getDemandTiming(demand: MatterSettlementDemand) {
  const demandSentAt = demand.dateSent ? dayjs(demand.dateSent) : null;
  const demandExpiresAt = demand.dateExpires ? dayjs(demand.dateExpires) : null;
  const daysFromExpiration =
    demandExpiresAt && demandExpiresAt.isValid()
      ? demandExpiresAt.startOf("day").diff(dayjs().startOf("day"), "day")
      : null;
  const isPastExpiration = daysFromExpiration != null && daysFromExpiration < 0;
  const expirationDayCount =
    daysFromExpiration != null ? Math.abs(daysFromExpiration) : null;
  const totalDemandWindowDays =
    demandExpiresAt &&
    demandExpiresAt.isValid() &&
    demandSentAt &&
    demandSentAt.isValid()
      ? Math.max(
          demandExpiresAt
            .startOf("day")
            .diff(demandSentAt.startOf("day"), "day"),
          1,
        )
      : null;
  const elapsedDemandWindowDays =
    totalDemandWindowDays != null && demandSentAt?.isValid()
      ? Math.min(
          Math.max(
            dayjs().startOf("day").diff(demandSentAt.startOf("day"), "day"),
            0,
          ),
          totalDemandWindowDays,
        )
      : null;
  const progressPercent =
    totalDemandWindowDays != null && elapsedDemandWindowDays != null
      ? Math.min(
          Math.max((elapsedDemandWindowDays / totalDemandWindowDays) * 100, 0),
          100,
        )
      : 0;
  return {
    demandSentAt,
    demandExpiresAt,
    expirationDayCount,
    isPastExpiration,
    progressPercent,
  };
}

function CoverageStatusBadge({
  label,
  tone,
}: {
  label: string;
  tone: PendingDemandCoverageGroup["coverageStatusTone"];
}) {
  return (
    <Text
      as="span"
      variant="caption"
      className={getPendingDemandStatusBadgeClass(tone)}
    >
      {label}
    </Text>
  );
}

export function CoverageTable({
  matter,
  groups,
  compactLimits,
  className,
  title = "Coverage",
  embedded = false,
}: {
  matter: Matter;
  groups: PendingDemandCoverageGroup[];
  compactLimits: boolean;
  className?: string;
  title?: string;
  embedded?: boolean;
}) {
  const { hasPagePermission } = useUser();
  const canViewParties = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailParties,
  );
  return (
    <section
      className={cn(
        "bg-card flex min-h-0 flex-col overflow-hidden rounded-[20px] border",
        embedded && "rounded-none border-0",
        className,
      )}
    >
      <div className="flex shrink-0 items-center justify-between border-b px-5 py-3.5">
        {embedded ? (
          <h3 className="min-w-0 text-sm font-semibold break-words">{title}</h3>
        ) : (
          <SectionLabel>{title}</SectionLabel>
        )}
        {canViewParties ? (
          <Link
            to="/matters/$matterId/matter-details/$tab"
            params={{ matterId: matter.id, tab: "client" }}
          >
            <Text
              variant="subHeading"
              color="primary"
              className="inline-flex items-center gap-0.5 hover:underline"
            >
              View all coverage
              <ChevronRight className="h-4 w-4" />
            </Text>
          </Link>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[560px]">
          <colgroup>
            <col className="w-[30%] min-w-[180px]" />
            <col className="w-[34%] min-w-[200px]" />
            <col className="w-[18%]" />
            <col className="w-[18%]" />
          </colgroup>
          <thead>
            <tr className="border-b text-left">
              <th className="min-w-[180px] px-4 py-3">
                <Text variant="caption" color="muted" className="font-semibold">
                  Carrier
                </Text>
              </th>
              <th className="min-w-[200px] px-4 py-3">
                <Text variant="caption" color="muted" className="font-semibold">
                  Limit
                </Text>
              </th>
              <th className="px-4 py-3 text-right">
                <Text variant="caption" color="muted" className="font-semibold">
                  Coverage status
                </Text>
              </th>
              <th className="px-4 py-3 text-right">
                <Text variant="caption" color="muted" className="font-semibold">
                  Liability status
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6">
                  <Text variant="subHeading" color="muted">
                    No coverage tracked yet.
                  </Text>
                </td>
              </tr>
            ) : null}
            {groups.map((group) => {
              const limitLines =
                group.limits.length > 0
                  ? group.limits
                  : [
                      {
                        key: `${group.key}-empty`,
                        limitLabel: "",
                        compactLimit: "—",
                        fullLimit: "—",
                      },
                    ];
              const rowSpan = limitLines.length;

              return limitLines.map((limitLine, lineIndex) => (
                <tr
                  key={`${group.key}-${limitLine.key}`}
                  className="border-b last:border-b-0"
                >
                  {lineIndex === 0 ? (
                    <td
                      rowSpan={rowSpan}
                      className="min-w-[180px] px-4 py-3.5 align-top"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <Text variant="body" className="min-w-0 font-semibold">
                          {group.carrier}
                        </Text>
                        <Text
                          as="span"
                          variant="caption"
                          color="muted"
                          className="bg-muted shrink-0 rounded-full px-2 py-0.5 leading-none"
                        >
                          {group.tier}
                        </Text>
                      </div>
                    </td>
                  ) : null}
                  <td className="min-w-[200px] px-4 py-3.5 align-top">
                    <div className="flex items-center gap-2">
                      <Text variant="body" className="font-semibold">
                        {compactLimits
                          ? limitLine.compactLimit
                          : limitLine.fullLimit}
                      </Text>
                      {limitLine.limitLabel ? (
                        <Text
                          as="span"
                          variant="caption"
                          color="muted"
                          className="bg-muted shrink-0 rounded-full px-2 py-0.5 leading-none"
                        >
                          {limitLine.limitLabel}
                        </Text>
                      ) : null}
                    </div>
                  </td>
                  {lineIndex === 0 ? (
                    <>
                      <td
                        rowSpan={rowSpan}
                        className="px-4 py-3.5 text-right align-top"
                      >
                        <CoverageStatusBadge
                          label={group.coverageStatus}
                          tone={group.coverageStatusTone}
                        />
                      </td>
                      <td
                        rowSpan={rowSpan}
                        className="px-4 py-3.5 text-right align-top"
                      >
                        <CoverageStatusBadge
                          label={group.liabilityStatus}
                          tone={group.liabilityStatusTone}
                        />
                      </td>
                    </>
                  ) : null}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DemandDetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <Text variant="caption" color="muted" className="font-medium">
        {label}
      </Text>
      <Text variant="body" className="mt-1 font-semibold break-words" as="p">
        {value}
      </Text>
    </div>
  );
}

function SentDemandCard({
  matter,
  demand,
  assignee,
}: {
  matter: Matter;
  demand: MatterSettlementDemand;
  assignee: ReturnType<typeof getDemandAssignee>;
}) {
  const { openCommunicationModal } = useGlobalModal();
  const { expirationDayCount, isPastExpiration, progressPercent } =
    getDemandTiming(demand);
  const insuranceSummary = getDemandInsuranceSummary(matter, demand);
  const adjusters = getDemandInsuranceAdjusters(matter, demand);
  const acceptedOffer = getAcceptedOfferForDemand(matter, demand);
  const acceptedOfferBadgeLabel = acceptedOffer
    ? getAcceptedOfferBadgeLabel(demand, acceptedOffer)
    : null;
  const { hasPagePermission } = useUser();
  const canViewSettlements = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailSettlements,
  );
  const canViewContactCommunications = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailTextMessages,
  );

  const handleTextAdjuster = (contactId?: string) => {
    if (!canViewContactCommunications || !contactId) {
      return;
    }

    openCommunicationModal({
      contactId,
      matterId: matter.id,
      setSelectedContactId: () => {},
    });
  };

  const handleEmailAdjuster = (emailAddress?: string) => {
    if (!emailAddress || typeof window === "undefined") {
      return;
    }

    window.location.href = `mailto:${emailAddress}`;
  };

  return (
    <div className="bg-card @container overflow-hidden rounded-[20px] border">
      <div className="flex flex-col">
        <div className="min-w-0">
          <div className="flex flex-col gap-3 px-5 pt-5 pb-5 @2xl:flex-row @2xl:items-start @2xl:justify-between @2xl:gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Text
                  as="p"
                  variant="title"
                  className="leading-tight break-words sm:text-2xl"
                >
                  {demand.policyLimit
                    ? `${formatCurrency(demand.amount)} — Policy Limits`
                    : formatCurrency(demand.amount)}
                </Text>
                {acceptedOfferBadgeLabel ? (
                  <Badge className="h-6 shrink-0 bg-emerald-100 text-emerald-800">
                    {acceptedOfferBadgeLabel}
                  </Badge>
                ) : null}
              </div>
              {insuranceSummary ? (
                <Text
                  as="p"
                  variant="subHeading"
                  color="muted"
                  className="mt-2 break-words"
                >
                  {insuranceSummary}
                </Text>
              ) : null}
              <Text as="p" variant="subHeading" className="mt-2 break-words">
                {`Sent ${formatDate(demand.dateSent)}`}
              </Text>
              {assignee ? (
                <Text
                  as="p"
                  variant="subHeading"
                  className="mt-1 break-words"
                >{`${assignee.role.label}: ${formatUsersName(assignee.user)}`}</Text>
              ) : null}
              <Text as="p" variant="subHeading" className="mt-1 break-words">
                {`Expires ${formatDate(demand.dateExpires)}`}
              </Text>
            </div>

            {canViewSettlements ? (
              <Link
                to="/matters/$matterId/settlements"
                params={{ matterId: matter.id }}
                className="shrink-0 self-start @2xl:self-auto"
              >
                <Text
                  variant="subHeading"
                  color="primary"
                  className="inline-flex items-center gap-0.5 whitespace-nowrap hover:underline"
                >
                  View demands
                  <ChevronRight className="h-4 w-4" />
                </Text>
              </Link>
            ) : null}
          </div>

          <div className="divide-y border-t">
            {(adjusters.length > 0
              ? adjusters
              : [
                  {
                    label: "Adjustor",
                    name: "—",
                    formattedPhone: "—",
                    phoneNumber: undefined,
                    contactId: undefined,
                    emailAddress: undefined,
                  },
                ]
            ).map((adjuster) => (
              <div
                key={adjuster.label}
                className="flex flex-col gap-4 px-5 py-4 @3xl:flex-row @3xl:items-end @3xl:justify-between"
              >
                <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-4 gap-y-4 @lg:grid-cols-2">
                  <DemandDetailField
                    label={adjuster.label}
                    value={adjuster.name}
                  />
                  <DemandDetailField
                    label="Phone"
                    value={adjuster.formattedPhone}
                  />
                </div>

                <div className="flex shrink-0 flex-wrap gap-2">
                  <HeaderButton
                    icon={<MessageSquareText className="mr-1 h-4 w-4" />}
                    label="Text"
                    onClick={() => {
                      handleTextAdjuster(adjuster.contactId);
                    }}
                    disabled={
                      !canViewContactCommunications || !adjuster.contactId
                    }
                  />
                  <HeaderButton
                    icon={<Phone className="mr-1 h-4 w-4" />}
                    label="Call"
                    onClick={() => {
                      openAdjusterPhoneCall(adjuster.phoneNumber);
                    }}
                    disabled={!adjuster.phoneNumber}
                  />
                  <HeaderButton
                    icon={<Mail className="mr-1 h-4 w-4" />}
                    label="Email"
                    onClick={() => {
                      handleEmailAdjuster(adjuster.emailAddress);
                    }}
                    disabled={!adjuster.emailAddress}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t px-5 py-5">
          <div className="flex flex-col gap-5 @3xl:flex-row @3xl:items-center @3xl:justify-between @3xl:gap-8">
            <div>
              <div className="flex items-baseline gap-2">
                <Text
                  as="p"
                  variant="title"
                  color="foreground"
                  className="text-4xl leading-none"
                >
                  {expirationDayCount != null ? expirationDayCount : "—"}
                </Text>
                <div>
                  <Text
                    as="p"
                    variant="cardTitle"
                    color="foreground"
                    className="leading-none"
                  >
                    {expirationDayCount === 1 ? "day" : "days"}
                  </Text>
                  <Text as="p" variant="subHeading" className="mt-1">
                    {isPastExpiration ? "since expiration" : "until expiration"}
                  </Text>
                </div>
              </div>
            </div>

            <div className="min-w-0 flex-1 @3xl:max-w-md">
              <div className="bg-muted h-1.5 overflow-hidden rounded-full">
                <div
                  className="h-full rounded-full bg-orange-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between">
                <Text variant="caption" color="muted">
                  Sent
                </Text>
                <Text variant="caption" color="muted">
                  Expires
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SentDemands({
  matter,
  title = "Sent demands",
  embedded = false,
}: {
  matter: Matter;
  title?: string;
  embedded?: boolean;
}) {
  const [, setClock] = useState(() => Date.now());
  useEffect(() => {
    const interval = window.setInterval(() => {
      setClock(Date.now());
    }, 60_000);
    return () => {
      window.clearInterval(interval);
    };
  }, []);
  const assignee = getDemandAssignee(matter);
  const sentDemands = getSentSettlementDemands(matter);
  return (
    <section className="min-w-0">
      {embedded ? (
        <h3 className="border-b pb-4 text-sm font-semibold break-words">
          {title}
        </h3>
      ) : (
        <SectionLabel>{title}</SectionLabel>
      )}

      {sentDemands.length > 0 ? (
        <div className="mt-3 space-y-3">
          {sentDemands.map((demand) => (
            <SentDemandCard
              key={demand.id}
              matter={matter}
              demand={demand}
              assignee={assignee}
            />
          ))}
        </div>
      ) : (
        <div className="bg-card mt-3 overflow-hidden rounded-[20px] border px-5 py-5">
          <Text variant="cardTitle" as="p">
            No demand sent yet
          </Text>
          <Text variant="subHeading" className="mt-2" as="p">
            This section will populate once a settlement demand is sent.
          </Text>
        </div>
      )}
    </section>
  );
}
